import React from 'react';
import styled from 'styled-components';
import { WorkspaceMember } from './WorkspaceMembersStep';
import { Headers } from '@tw/tw-component-library';
import {
  FormattedClientData,
  FormattedMatterData,
} from '../../../../hooks/data/clientMatter/useClientMatterSearch';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.secondary || '#666'};
  margin-bottom: 1rem;
`;

const ReviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReviewItem = styled.div`
  padding: 0.5rem 0;
  background-color: ${({ theme }) => theme.colors.background || '#f5f5f5'};
  border-radius: 4px;
`;

const ReviewValue = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.secondary || '#333'};
`;

const MembersList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.25rem;
`;

const MemberTag = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  font-size: 0.85rem;
  gap: 0.25rem;
`;

const MemberName = styled.span`
  color: ${({ theme }) => theme.colors.primary || '#0078d4'};
  font-weight: 500;
`;

const MemberEmail = styled.span`
  color: ${({ theme }) => theme.colors.secondary || '#666'};
  font-size: 0.75rem;
`;

const AdminBadge = styled.span`
  display: inline-block;
  padding: 0.125rem 0.5rem;
  background-color: ${({ theme }) => theme.colors.primary || '#0078d4'};
  color: white;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  margin-top: 0.25rem;
  align-self: flex-start;
`;

const EmptyValue = styled.span`
  color: ${({ theme }) => theme.colors.secondary || '#999'};
  font-style: italic;
`;

export interface WorkspaceReviewStepProps {
  workspaceData: {
    type?: 'client-matter' | 'non-client-matter';
    name: string;
    description: string;
    template?: 'm&a' | 'litigation' | 'blank' | 'custom';
    members: WorkspaceMember[];
    matterId?: string;
    clientId?: string;
    client?: FormattedClientData;
    matter?: FormattedMatterData;
  };
}

export const WorkspaceReviewStep: React.FC<WorkspaceReviewStepProps> = ({
  workspaceData,
}) => {
  const getTypeLabel = (type?: 'client-matter' | 'non-client-matter') => {
    switch (type) {
      case 'client-matter':
        return 'Client-Matter Workspace';
      case 'non-client-matter':
        return 'Non-Matter Workspace';
      default:
        return 'Not selected';
    }
  };

  return (
    <Container data-tour="create-ws-review-section">
      <ReviewSection>
        <Headers headerType="h2">Review Your Workspace</Headers>
        <Description>Please review your workspace details before creating</Description>
      </ReviewSection>

      <ReviewSection>
        <ReviewItem>
          <Headers headerType="h3">Workspace Type</Headers>
          <ReviewValue>{getTypeLabel(workspaceData.type)}</ReviewValue>
        </ReviewItem>

        {workspaceData.type === 'client-matter' && (
          <>
            {(workspaceData.client || workspaceData.clientId) && (
              <ReviewItem>
                <Headers headerType="h3">Client</Headers>
                <ReviewValue>
                  {workspaceData.client?.label || workspaceData.clientId}
                </ReviewValue>
              </ReviewItem>
            )}
            {(workspaceData.matter || workspaceData.matterId) && (
              <ReviewItem>
                <Headers headerType="h3">Matter</Headers>
                <ReviewValue>
                  {workspaceData.matter?.label || workspaceData.matterId}
                </ReviewValue>
              </ReviewItem>
            )}
          </>
        )}

        <ReviewItem>
          <Headers headerType="h3">Workspace Name</Headers>
          <ReviewValue>
            {workspaceData.name || <EmptyValue>Not provided</EmptyValue>}
          </ReviewValue>
        </ReviewItem>

        <ReviewItem>
          <Headers headerType="h3">Description</Headers>
          <ReviewValue>
            {workspaceData.description || <EmptyValue>No description</EmptyValue>}
          </ReviewValue>
        </ReviewItem>

        <ReviewItem>
          <Headers headerType="h3">Members</Headers>
          {workspaceData.members.length > 0 ? (
            <MembersList>
              {workspaceData.members.map((member) => {
                const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.username || member.id;
                return (
                  <MemberTag key={member.id}>
                    <MemberName>{fullName}</MemberName>
                    {member.email && <MemberEmail>{member.email}</MemberEmail>}
                    {member.isOwner && <AdminBadge>Owner</AdminBadge>}
                  </MemberTag>
                );
              })}
            </MembersList>
          ) : (
            <ReviewValue>
              <EmptyValue>No members added</EmptyValue>
            </ReviewValue>
          )}
        </ReviewItem>
      </ReviewSection>
    </Container>
  );
};

